import { Fragment } from "react";
import { userReportsApi } from "../../api";
import { useAsync } from "../../hooks";
import { Accordion, LoadingText, Modal } from "../ui";
import { formatTime, getDateGroupLabel, lineDiff } from "../../utils";
import type {
  ChangelogFieldDiff,
  DashboardChangelogAction,
  DashboardChangelogEntry,
} from "../../types/reports";

interface DashboardChangelogModalProps {
  dashboardId: string;
  dashboardName?: string;
  onClose: () => void;
}

const VERBS: Record<DashboardChangelogAction, string> = {
  "dashboard.created": "created the dashboard",
  "dashboard.renamed": "renamed the dashboard",
  "widget.added": "added widget",
  "widget.removed": "removed widget",
  "widget.reconfigured": "reconfigured widget",
  "widget.edited": "edited widget",
};

const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  description: "Description",
  sql_query: "SQL query",
  chart_type: "Chart type",
  x_col: "X axis",
  y_col: "Y axis",
};

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

function actorName(entry: DashboardChangelogEntry): string {
  return entry.actor?.full_name || entry.actor_name || "Someone";
}

function summarize(entry: DashboardChangelogEntry): string {
  const verb = VERBS[entry.action] ?? "changed the dashboard";
  const target = entry.widget_name ? ` “${entry.widget_name}”` : "";
  return `${actorName(entry)} ${verb}${target}`;
}

function SqlDiff({ oldText, newText }: { oldText: string; newText: string }) {
  return (
    <pre className="mt-1 overflow-x-auto rounded bg-gray-900 p-2 font-mono text-[11px] leading-relaxed">
      {lineDiff(oldText, newText).map((op, i) => {
        const cls =
          op.type === "add" ? "text-green-300" : op.type === "del" ? "text-red-300" : "text-gray-300";
        const marker = op.type === "add" ? "+" : op.type === "del" ? "-" : " ";
        return (
          <div key={i} className={cls}>
            {marker} {op.text}
          </div>
        );
      })}
    </pre>
  );
}

function FieldDiff({ diff }: { diff: ChangelogFieldDiff }) {
  const label = fieldLabel(diff.field);
  if (diff.field === "sql_query") {
    return (
      <div className="text-xs">
        <p className="font-medium text-gray-500">{label}</p>
        <SqlDiff oldText={diff.old ?? ""} newText={diff.new ?? ""} />
      </div>
    );
  }
  return (
    <div className="text-xs">
      <p className="font-medium text-gray-500">{label}</p>
      <p className="mt-0.5 break-words">
        <span className="text-red-500 line-through">{diff.old || "(empty)"}</span>
        <span className="text-gray-400"> → </span>
        <span className="text-green-600">{diff.new || "(empty)"}</span>
      </p>
    </div>
  );
}

export default function DashboardChangelogModal({
  dashboardId,
  dashboardName,
  onClose,
}: DashboardChangelogModalProps) {
  const { data, loading, error } = useAsync<DashboardChangelogEntry[]>(
    () => userReportsApi.dashboardChangelog(dashboardId).then((res) => res.data),
    [dashboardId],
    { errorMessage: "Failed to load change history." },
  );

  const entries = data ?? [];
  const now = new Date();

  return (
    <Modal onClose={onClose} size="xl" title="Change history" subtitle={dashboardName}>
      {loading ? (
        <LoadingText />
      ) : error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-400">No changes recorded yet.</p>
      ) : (
        <div className="space-y-4">
          {entries.map((entry, index) => {
            const group = getDateGroupLabel(entry.created_at, now);
            const prevGroup = index > 0 ? getDateGroupLabel(entries[index - 1].created_at, now) : null;
            const header = group !== prevGroup ? group : null;
            const fields = entry.details?.fields ?? [];
            const renamed = entry.action === "dashboard.renamed" ? entry.details : null;
            return (
              <Fragment key={entry.id}>
                {header && (
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{header}</p>
                )}
                <div className="text-sm">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-gray-700">{summarize(entry)}</span>
                    <time className="shrink-0 text-xs tabular-nums text-gray-400">
                      {formatTime(entry.created_at)}
                    </time>
                  </div>

                  {renamed && (
                    <p className="mt-0.5 text-xs break-words">
                      <span className="text-red-500 line-through">{renamed.old || "(empty)"}</span>
                      <span className="text-gray-400"> → </span>
                      <span className="text-green-600">{renamed.new || "(empty)"}</span>
                    </p>
                  )}

                  {fields.length > 0 && (
                    <div className="mt-1.5">
                      <Accordion title="What changed" defaultOpen={false}>
                        <div className="space-y-2">
                          {fields.map((diff, i) => (
                            <FieldDiff key={i} diff={diff} />
                          ))}
                        </div>
                      </Accordion>
                    </div>
                  )}
                </div>
              </Fragment>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
